import { useAuth } from "@/components/auth/AuthProvider";
import { AuthForm } from "@/components/auth/AuthForm";
import { LoadingState } from "@/components/admin/LoadingState";
import { useLocation } from "react-router-dom";
import { AuthHero } from "@/components/auth/AuthHero";
import { LoadingAuthentication } from "@/components/auth/LoadingAuthentication";
import { TokenErrorDisplay } from "@/components/auth/TokenErrorDisplay";
import { useMagicLinkProcessor } from "@/components/auth/hooks/useMagicLinkProcessor";
import { useHashAuthentication } from "@/components/auth/hooks/useHashAuthentication";
import { useAuthRedirection } from "@/components/auth/hooks/useAuthRedirection";
import { useEffect, useState, useRef } from "react";
import { forceRedirect } from "@/integrations/supabase/client";
import { toast } from "sonner";
const Auth = () => {
  const {
    user,
    loading,
    profile,
    profileLoaded
  } = useAuth();
  const location = useLocation();
  const [authInitialized, setAuthInitialized] = useState(false);
  const [showProfileError, setShowProfileError] = useState(false);
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  const [forceRedirectInitiated, setForceRedirectInitiated] = useState(false);
  const userInitializedTimestampRef = useRef<number | null>(null);
  const {
    processingMagicLink,
    tokenError,
    setTokenError
  } = useMagicLinkProcessor();
  const {
    processingHash
  } = useHashAuthentication();
  const isPasswordReset = location.search.includes('type=recovery') || location.hash.includes('type=recovery');
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const userContext = params.get('user_context');
    const orgContext = params.get('org_context');
    if (userContext || orgContext) {
      console.log(`Auth page received context parameters - user: ${userContext}, org: ${orgContext}`);
      if (!user) {
        if (userContext) localStorage.setItem('pendingUserContext', userContext);
        if (orgContext) localStorage.setItem('pendingOrgContext', orgContext);
        const fullPath = window.location.pathname + window.location.search;
        localStorage.setItem('pendingRedirectAfterAuth', fullPath);
        if (window.history && window.history.replaceState) {
          const cleanParams = new URLSearchParams(params);
          cleanParams.delete('user_context');
          cleanParams.delete('org_context');
          const newUrl = `${window.location.pathname}${cleanParams.toString() ? `?${cleanParams.toString()}` : ''}`;
          window.history.replaceState({}, document.title, newUrl);
        }
      }
    }
  }, [location.search, user]);
  const {
    redirecting
  } = useAuthRedirection(user, loading, processingMagicLink || processingHash, isPasswordReset, tokenError);
  useEffect(() => {
    if (user && !userInitializedTimestampRef.current) {
      userInitializedTimestampRef.current = Date.now();
      console.log(`User initialized at ${new Date().toISOString()}`);
    }
  }, [user]);
  useEffect(() => {
    console.log('Auth page state:', {
      hasUser: !!user,
      userId: user?.id,
      loading,
      processingMagicLink,
      processingHash,
      redirecting,
      profileLoaded,
      hasProfile: !!profile,
      showProfileError,
      authInitialized,
      isPasswordReset,
      tokenError,
      forceRedirectInitiated,
      timeNow: new Date().toISOString(),
      timeWithUser: userInitializedTimestampRef.current ? Math.floor((Date.now() - userInitializedTimestampRef.current) / 1000) + 's' : 'n/a'
    });
  }, [user, loading, processingMagicLink, processingHash, redirecting, profileLoaded, profile, showProfileError, authInitialized, isPasswordReset, tokenError, forceRedirectInitiated]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setAuthInitialized(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (loading && !loadingTimedOut) {
      const timer = setTimeout(() => {
        console.log('Auth loading timed out after 5 seconds');
        setLoadingTimedOut(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [loading, loadingTimedOut]);
  useEffect(() => {
    if (user && profileLoaded && !profile && !isPasswordReset) {
      const timer = setTimeout(() => {
        setShowProfileError(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setShowProfileError(false);
    }
  }, [user, profile, profileLoaded, isPasswordReset]);
  useEffect(() => {
    if (user && !redirecting && !forceRedirectInitiated && authInitialized) {
      const delay = userInitializedTimestampRef.current ? Math.max(2000, 5000 - (Date.now() - userInitializedTimestampRef.current)) : 5000;
      console.log(`Setting emergency redirect timer for ${delay}ms`);
      const timer = setTimeout(() => {
        console.log('EMERGENCY REDIRECT: User still on auth page after timeout');
        setForceRedirectInitiated(true);
        toast.info("Finalizing login...", {
          id: "force-redirect-toast",
          duration: 3000
        });
        const pendingRedirect = localStorage.getItem('pendingRedirectAfterAuth');
        const returnPath = pendingRedirect || location.state?.returnTo || new URLSearchParams(window.location.search).get('returnTo') || '/settings';
        localStorage.removeItem('pendingRedirectAfterAuth');
        forceRedirect(returnPath);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [user, redirecting, forceRedirectInitiated, authInitialized, location.state]);
  const handleTokenErrorReset = () => {
    setTokenError(null);
    window.location.href = '/auth';
  };
  if (!authInitialized) {
    return <div className="min-h-screen flex items-center justify-center">
        <LoadingState />
      </div>;
  }
  if (processingMagicLink || processingHash) {
    return <LoadingAuthentication message="Verifying your authentication..." />;
  }
  if (redirecting || forceRedirectInitiated) {
    return <LoadingAuthentication message="Logging you in..." />;
  }
  if (tokenError) {
    return <TokenErrorDisplay error={tokenError} onReset={handleTokenErrorReset} />;
  }
  const shouldShowLoading = loading && !loadingTimedOut;
  if (shouldShowLoading) {
    return <div className="min-h-screen flex items-center justify-center">
        <LoadingState />
      </div>;
  }
  if (showProfileError) {
    return <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50">
        <div className="w-full max-w-md bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Profile Error</h2>
          <p className="text-gray-700 mb-4">
            We couldn't find your user profile. Please contact support or try signing in again.
          </p>
          <div className="flex justify-end">
            <button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 mr-2">
              Retry
            </button>
            <button onClick={() => {
            sessionStorage.clear();
            localStorage.removeItem('lastProfileId');
            localStorage.removeItem('lastProfileRole');
            localStorage.removeItem('lastOrganizationId');
            localStorage.removeItem('lastOrganizationName');
            window.location.href = '/auth';
          }} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
              Sign Out
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen flex flex-col md:flex-row items-stretch bg-black">
      <AuthHero />
      <div className="w-full md:w-1/2">
        <div className="h-full flex items-center justify-center p-8">
          <div className="w-full max-w-md space-y-6 p-8 shadow-xl rounded-md bg-neutral-950">
            <AuthForm />
          </div>
        </div>
      </div>
    </div>;
};
export default Auth;